import React, { useRef, useState, useEffect, useCallback } from "react";
import { BsChevronCompactLeft, BsChevronCompactRight } from "react-icons/bs";
import * as s from "./SwiperStyled";

type SwipeListProps = {
  items: React.ReactNode[];
  SwiperWrapper: React.ForwardRefExoticComponent<
    React.HTMLAttributes<HTMLDivElement> & React.RefAttributes<HTMLDivElement>
  >;

  SwiperItem: React.FC<React.HTMLAttributes<HTMLDivElement>>;
};

const SwipeList: React.FC<SwipeListProps> = ({
  items,
  SwiperWrapper,
  SwiperItem,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [startX, setStartX] = useState<number | null>(null);
  const [currentTranslate, setCurrentTranslate] = useState<number>(0);
  const [prevTranslate, setPrevTranslate] = useState<number>(0);
  const [dragging, setDragging] = useState<boolean>(false);
  const itemPositions = useRef<number[]>([]); // 각 아이템의 offsetLeft 위치
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [moving, setMoving] = useState<boolean>(false);

  const handlePointerDown = (clientX: number) => {
    setStartX(clientX);
    setDragging(true);
    setPrevTranslate(currentTranslate); // 현재 위치 저장
  };

  const handleNext = () => {
    const scrollX = -currentTranslate; // 현재 위치 (스크롤 기준)
    const nextIndex = itemPositions.current.findIndex(
      (pos) => pos > scrollX + 1
    );

    if (nextIndex !== -1) {
      const nextPos = itemPositions.current[nextIndex];
      setCurrentTranslate(-nextPos);
      setPrevTranslate(-nextPos);
    }
  };

  const handlePrev = () => {
    const scrollX = -currentTranslate;
    const reversed = [...itemPositions.current].reverse();
    const prevIndex = reversed.findIndex((pos) => pos < scrollX - 1);
    if (prevIndex !== -1) {
      const actualIndex = itemPositions.current.length - 1 - prevIndex;
      const prevPos = itemPositions.current[actualIndex];
      setCurrentTranslate(-prevPos);
      setPrevTranslate(-prevPos);
    }
  };

  const handlePointerMove = useCallback(
    (clientX: number) => {
      if (!dragging || startX === null) return;
      setMoving(true);
      const delta = clientX - startX;
      let nextTranslate = prevTranslate + delta;
      setCurrentTranslate(nextTranslate);
    },
    [dragging, startX, prevTranslate]
  );

  const handlePointerUp = () => {
    if (!dragging) return;
    setStartX(null);
    setDragging(false);
    setMoving(false);

    const scrollX = -currentTranslate;
    const threshold = 20;

    const closest = itemPositions.current.reduce((prev, curr) =>
      Math.abs(curr - scrollX) < Math.abs(prev - scrollX) ? curr : prev
    );

    const distance = Math.abs(closest + currentTranslate);
    // console.log(distance, threshold, closest);
    if (distance < threshold) {
      setCurrentTranslate(currentTranslate);
      setPrevTranslate(currentTranslate);
    } else {
      setCurrentTranslate(-closest);
      setPrevTranslate(-closest);
    }
  };

  const onMouseDown = (e: React.MouseEvent) => handlePointerDown(e.clientX);
  const onTouchEnd = () => handlePointerUp();
  const [maxTranslate, setMaxTranslate] = useState<number>(0);

  useEffect(() => {
    if (listRef.current) {
      const children = Array.from(listRef.current.children) as HTMLDivElement[];
      const positions: number[] = [];
      let acc = 0;
      for (const child of children) {
        positions.push(acc);
        acc += child.getBoundingClientRect().width + 12;
      }
      itemPositions.current = positions;
    }
    const wrapperWidth = wrapperRef.current?.clientWidth ?? 0;
    const listWidth = listRef.current?.scrollWidth ?? 0;
    const max = wrapperWidth - listWidth;
    setMaxTranslate(max < 0 ? max : 0);
  }, [items]);

  return (
    <SwiperWrapper ref={wrapperRef} style={s.WrapperStyle}>
      <s.SwiperList
        ref={listRef}
        onMouseDown={onMouseDown}
        onMouseMove={(e) => dragging && handlePointerMove(e.clientX)}
        onMouseUp={() => handlePointerUp()}
        onMouseLeave={() => handlePointerUp()}
        onTouchStart={(e) => handlePointerDown(e.touches[0].clientX)}
        onTouchMove={(e) => handlePointerMove(e.touches[0].clientX)}
        onTouchEnd={onTouchEnd}
        isDragging={dragging}
        currentTranslate={currentTranslate}
        maxTranslate={maxTranslate}
      >
        {items.map((item, index) => (
          <SwiperItem key={index} style={{ flexShrink: 0 }}>
            {item}
          </SwiperItem>
        ))}
        {dragging && moving && (
          <s.DragTrack
            onMouseUp={() => handlePointerUp()}
            onMouseLeave={() => handlePointerUp()}
          />
        )}
      </s.SwiperList>
      {currentTranslate > maxTranslate && (
        <s.SwiperButton
          onClick={() => handleNext()}
          next
          data-testid="blocks-swiper-swiper-swiper-button-next"
        >
          <BsChevronCompactRight size={30} />
        </s.SwiperButton>
      )}

      {currentTranslate < 0 && (
        <s.SwiperButton
          onClick={() => handlePrev()}
          data-testid="blocks-swiper-swiper-swiper-button-prev"
        >
          <BsChevronCompactLeft size={30} />
        </s.SwiperButton>
      )}
    </SwiperWrapper>
  );
};

export default SwipeList;
